// src/lib/supabase.js
// Data-access layer wrapping Supabase calls.
// v2.2: adds attachments table + storage helpers.

import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !key || url.includes('YOUR-PROJECT') || key.includes('your-anon')) {
  alert(
    'Supabase credentials not configured.\n\n' +
    'Copy .env.example to .env and fill in your real Supabase URL and anon key, ' +
    'then restart the dev server.'
  );
  throw new Error('Missing Supabase credentials');
}

export const supabase = createClient(url, key, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

export const ATTACHMENTS_BUCKET = 'attachments';
export const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10 MB

// -------------------------------------------------------------
// Auth helpers
// -------------------------------------------------------------
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}
export async function signIn(email, password) {
  return supabase.auth.signInWithPassword({ email, password });
}
export async function signOut() {
  return supabase.auth.signOut();
}
export function onAuthChange(cb) {
  return supabase.auth.onAuthStateChange(cb);
}
// Send a password-recovery email. After the user clicks the link in the email,
// they land back on the site with a recovery session — at that point the
// onAuthStateChange handler in main.js fires PASSWORD_RECOVERY and we show
// the "set new password" screen. redirectTo uses the current origin so the
// link works on both localhost and production without configuration.
export async function sendPasswordRecovery(email) {
  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin
  });
}

// -------------------------------------------------------------
// Generic CRUD
// -------------------------------------------------------------
async function list(table, opts = {}) {
  let q = supabase.from(table).select('*');
  if (opts.order) {
    q = q.order(opts.order.column, { ascending: opts.order.ascending ?? false });
  } else {
    q = q.order('created_at', { ascending: false });
  }
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}
async function insert(table, row) {
  const { data, error } = await supabase.from(table).insert(row).select().single();
  if (error) throw error;
  return data;
}
async function update(table, id, patch) {
  const { data, error } = await supabase.from(table).update(patch).eq('id', id).select().single();
  if (error) throw error;
  return data;
}
async function remove(table, id) {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) throw error;
}

// -------------------------------------------------------------
// Typed helpers
// -------------------------------------------------------------
export const db = {
  companies: {
    list: () => list('companies', { order: { column: 'name', ascending: true } }),
    create: (row) => insert('companies', row),
    update: (id, patch) => update('companies', id, patch),
    remove: (id) => remove('companies', id)
  },
  contacts: {
    list: () => list('contacts', { order: { column: 'last_name', ascending: true } }),
    create: (row) => insert('contacts', row),
    update: (id, patch) => update('contacts', id, patch),
    remove: (id) => remove('contacts', id)
  },
  deals: {
    list: () => list('deals'),
    create: (row) => insert('deals', row),
    update: (id, patch) => update('deals', id, patch),
    remove: (id) => remove('deals', id)
  },
  tickets: {
    list: () => list('tickets'),
    create: (row) => insert('tickets', row),
    update: (id, patch) => update('tickets', id, patch),
    remove: (id) => remove('tickets', id)
  },
  activities: {
    list: () => list('activities'),
    create: (row) => insert('activities', row),
    update: (id, patch) => update('activities', id, patch),
    remove: (id) => remove('activities', id)
  },
  attachments: {
    list: () => list('attachments'),
    create: (row) => insert('attachments', row),
    update: (id, patch) => update('attachments', id, patch),
    remove: (id) => remove('attachments', id)
  },
  profiles: {
    list: () => list('profiles', { order: { column: 'email', ascending: true } }),
    update: (id, patch) => update('profiles', id, patch)
  },
  settings: {
    async get(key) {
      const { data, error } = await supabase.from('settings').select('*').eq('key', key).maybeSingle();
      if (error) throw error;
      return data;
    },
    async upsert(key, value) {
      const { data, error } = await supabase
        .from('settings')
        .upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' })
        .select().single();
      if (error) throw error;
      return data;
    }
  }
};

// Update the current user's password. Requires an active session.
// Used by the first-login password setup screen.
export async function setUserPassword(newPassword) {
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  return data;
}

// -------------------------------------------------------------
// Edge Function: invite a new user
// Calls the deployed `invite-user` function with the current session token.
// -------------------------------------------------------------
export async function inviteUser(email, fullName, makeAdmin) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const { data, error } = await supabase.functions.invoke('invite-user', {
    body: { email, fullName, makeAdmin: !!makeAdmin }
  });
  if (error) {
    // Supabase wraps non-2xx responses; try to extract our JSON error message
    let msg = error.message || 'Invite failed';
    if (error.context && typeof error.context.json === 'function') {
      try {
        const body = await error.context.json();
        if (body && body.error) msg = body.error;
      } catch (_) { /* ignore */ }
    }
    throw new Error(msg);
  }
  if (data && data.error) throw new Error(data.error);
  return data;
}

// -------------------------------------------------------------
// Storage helpers
// -------------------------------------------------------------
export async function uploadAttachment(file, parentKind, parentId) {
  if (file.size > MAX_ATTACHMENT_SIZE) {
    throw new Error(`File too large. Max ${MAX_ATTACHMENT_SIZE / 1024 / 1024} MB.`);
  }
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${parentKind}/${parentId}/${Date.now()}_${safeName}`;

  const { error: upErr } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false });
  if (upErr) throw upErr;

  const row = {
    file_name: file.name,
    file_size: file.size,
    mime_type: file.type || null,
    storage_path: path
  };
  if (parentKind === 'company') row.company_id = parentId;
  if (parentKind === 'deal')    row.deal_id    = parentId;
  if (parentKind === 'ticket')  row.ticket_id  = parentId;

  const { data: rec, error: insErr } = await supabase
    .from('attachments').insert(row).select().single();
  if (insErr) {
    await supabase.storage.from(ATTACHMENTS_BUCKET).remove([path]);
    throw insErr;
  }
  return rec;
}

export async function getAttachmentSignedUrl(storagePath, opts = {}) {
  const { data, error } = await supabase.storage
    .from(ATTACHMENTS_BUCKET)
    .createSignedUrl(storagePath, 60, { download: !!opts.download });
  if (error) throw error;
  return data.signedUrl;
}

export async function deleteAttachmentFile(id, storagePath) {
  await supabase.storage.from(ATTACHMENTS_BUCKET).remove([storagePath]);
  const { error } = await supabase.from('attachments').delete().eq('id', id);
  if (error) throw error;
}

// -------------------------------------------------------------
// Load entire workspace in parallel
// -------------------------------------------------------------
export async function loadWorkspace() {
  const [companies, contacts, deals, tickets, activities, attachments, profiles, fxSettings] = await Promise.all([
    db.companies.list(),
    db.contacts.list(),
    db.deals.list(),
    db.tickets.list(),
    db.activities.list(),
    db.attachments.list(),
    db.profiles.list().catch(() => []),    // graceful fallback if profiles table not yet migrated
    db.settings.get('fx_rates').catch(() => null)
  ]);
  return { companies, contacts, deals, tickets, activities, attachments, profiles, fxSettings };
}
